import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { contractorApi } from '../../lib/contractorApi';

interface WinRateData {
  period_days: number;
  lead_performance: { total: number; accepted: number; passed: number; expired: number; acceptance_pct: number };
  response_time: { avg_hours: number | null; jobs_tracked: number };
  best_time: { day_of_week: string | null; hour_of_day: number | null; wins: number };
  revenue_by_trade: { trade: string; jobs: number; avg_value: number | null; total: number }[];
  top_zip_codes: { zip_code: string; job_count: number; revenue: number }[];
  benchmarks: { your_win_rate: number | null; area_win_rate: number | null; your_avg_response_hours: number | null; area_avg_response_hours: number | null; area_avg_job_value: number | null };
  seasonal_forecast: { category: string; upcoming_count: number }[];
}

interface AnalyticsData {
  period_days: number;
  leads: {
    total: number;
    new: number;
    accepted: number;
    passed: number;
    expired: number;
    credits_spent: number;
    acceptance_rate: number;
  };
  jobs: {
    total: number;
    won_count: number;
    completed_count: number;
    lost_count: number;
    quoted_count: number;
    avg_job_value: number | null;
    total_revenue: number;
    win_rate: number;
  };
  credits: {
    balance: number;
    credits_spent: number;
  };
  reviews: {
    total: number;
    avg_overall: number | null;
    avg_quality: number | null;
    avg_communication: number | null;
    avg_timeliness: number | null;
    avg_price_fairness: number | null;
  };
}

interface MonthlyRow {
  month: string;
  jobs: number;
  won: number;
  revenue: number;
}

const PERIODS = [
  { label: '7d',  days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: '1y',  days: 365 },
];

function StatCard({ label, value, sub, accent = '#1D4ED8' }: { label: string; value: string; sub?: string; accent?: string }) {
  return (
    <div style={{ background: '#fff', borderRadius: 14, padding: '14px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
      <p style={{ margin: '0 0 4px', fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </p>
      <p style={{ margin: 0, fontSize: 22, fontWeight: 800, color: accent }}>{value}</p>
      {sub && <p style={{ margin: '2px 0 0', fontSize: 11, color: '#9ca3af' }}>{sub}</p>}
    </div>
  );
}

function RatingRow({ label, value }: { label: string; value: number | null }) {
  if (value === null) return null;
  const pct = Math.round((value / 5) * 100);
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 13, color: '#374151' }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{value.toFixed(1)}</span>
      </div>
      <div style={{ height: 6, background: '#f3f4f6', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: '#f59e0b', borderRadius: 99, transition: 'width 0.5s ease' }} />
      </div>
    </div>
  );
}

function BarChart({ rows }: { rows: MonthlyRow[] }) {
  if (rows.length === 0) {
    return (
      <div style={{ padding: '24px 0', textAlign: 'center' }}>
        <p style={{ margin: 0, fontSize: 13, color: '#9ca3af' }}>No job data yet.</p>
      </div>
    );
  }

  const maxRevenue = Math.max(...rows.map(r => r.revenue), 1);
  const chartHeight = 100;

  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, minWidth: rows.length * 48, padding: '0 4px 8px' }}>
        {rows.map(row => {
          const h = Math.max(Math.round((row.revenue / maxRevenue) * chartHeight), row.revenue > 0 ? 4 : 2);
          return (
            <div key={row.month} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 40 }}>
              <span style={{ fontSize: 10, color: '#6b7280', marginBottom: 4 }}>
                {row.revenue > 0 ? `$${Math.round(row.revenue / 1000)}k` : ''}
              </span>
              <div style={{
                width: '100%', maxWidth: 32, height: h, minHeight: 2,
                background: row.revenue > 0 ? '#1D4ED8' : '#e5e7eb',
                borderRadius: '4px 4px 0 0', transition: 'height 0.4s ease',
              }} />
              <span style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>
                {row.month.slice(5)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ContractorAnalytics() {
  const navigate = useNavigate();
  const [activePeriod, setActivePeriod] = useState(30);

  const { data: analyticsRes, isLoading } = useQuery({
    queryKey: ['contractor-analytics', activePeriod],
    queryFn: () => contractorApi.get<{ data: AnalyticsData }>(`/contractor/analytics?days=${activePeriod}`),
  });

  const { data: monthlyRes } = useQuery({
    queryKey: ['contractor-analytics-monthly'],
    queryFn: () => contractorApi.get<{ data: MonthlyRow[] }>('/contractor/analytics/monthly?months=6'),
  });

  const { data: winRateRes } = useQuery({
    queryKey: ['contractor-analytics-winrate', activePeriod],
    queryFn: () => contractorApi.get<{ data: WinRateData }>(`/contractor/analytics/win-rate?days=${activePeriod}`),
  });

  const stats = analyticsRes?.data;
  const monthly = monthlyRes?.data ?? [];
  const wr = winRateRes?.data;

  const creditMax = (stats?.credits.balance ?? 0) + (stats?.credits.credits_spent ?? 0);
  const creditPct = creditMax > 0 ? Math.round(((stats?.credits.balance ?? 0) / creditMax) * 100) : 0;

  return (
    <div style={{ minHeight: '100dvh', background: '#f9fafb' }}>
      {/* Header */}
      <div style={{ background: '#1D4ED8', padding: '48px 20px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          type="button"
          onClick={() => navigate('/app/contractor/dashboard')}
          style={{
            background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
            width: 36, height: 36, borderRadius: 10, cursor: 'pointer', fontSize: 18,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          &#8592;
        </button>
        <h1 style={{ color: '#fff', fontSize: 20, fontWeight: 700, margin: 0 }}>Analytics</h1>
      </div>

      {/* Period selector */}
      <div style={{ background: '#1D4ED8', padding: '0 16px 16px', display: 'flex', gap: 8 }}>
        {PERIODS.map(({ label, days }) => (
          <button
            key={days}
            type="button"
            onClick={() => setActivePeriod(days)}
            style={{
              padding: '6px 14px', border: 'none', borderRadius: 99,
              fontWeight: 600, fontSize: 13, cursor: 'pointer',
              background: activePeriod === days ? '#fff' : 'rgba(255,255,255,0.2)',
              color: activePeriod === days ? '#1D4ED8' : '#fff',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <div style={{ padding: '16px 16px 100px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <div style={{ width: 28, height: 28, border: '3px solid #1D4ED8', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : (
          <>
            {/* Lead stats */}
            <div>
              <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Leads
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <StatCard label="Total leads" value={String(stats?.leads.total ?? 0)} />
                <StatCard label="Accepted" value={String(stats?.leads.accepted ?? 0)} sub={`${stats?.leads.acceptance_rate ?? 0}% rate`} accent="#22c55e" />
                <StatCard label="Credits spent" value={String(stats?.leads.credits_spent ?? 0)} accent="#8b5cf6" />
                <StatCard label="Expired/Passed" value={String((stats?.leads.expired ?? 0) + (stats?.leads.passed ?? 0))} accent="#6b7280" />
              </div>
            </div>

            {/* Job stats */}
            <div>
              <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Jobs
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <StatCard label="Revenue" value={`$${(stats?.jobs.total_revenue ?? 0).toLocaleString()}`} accent="#1D4ED8" />
                <StatCard label="Win rate" value={`${stats?.jobs.win_rate ?? 0}%`} sub={`${stats?.jobs.completed_count ?? 0} completed`} accent="#22c55e" />
                <StatCard label="Avg job value" value={stats?.jobs.avg_job_value != null ? `$${Math.round(stats.jobs.avg_job_value).toLocaleString()}` : '—'} />
                <StatCard label="Lost jobs" value={String(stats?.jobs.lost_count ?? 0)} accent="#ef4444" />
              </div>
            </div>

            {/* Revenue bar chart */}
            <div style={{ background: '#fff', borderRadius: 16, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
              <p style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 700, color: '#111827' }}>
                Monthly revenue (6 months)
              </p>
              <BarChart rows={monthly} />
            </div>

            {/* Credit usage */}
            <div style={{ background: '#fff', borderRadius: 16, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#111827' }}>Credit balance</p>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#8b5cf6' }}>
                  {stats?.credits.balance ?? 0} remaining
                </span>
              </div>
              <div style={{ height: 10, background: '#f3f4f6', borderRadius: 99, overflow: 'hidden' }}>
                <div style={{
                  width: `${creditPct}%`, height: '100%',
                  background: 'linear-gradient(90deg, #8b5cf6, #1D4ED8)',
                  borderRadius: 99, transition: 'width 0.5s ease',
                }} />
              </div>
              <p style={{ margin: '6px 0 0', fontSize: 12, color: '#9ca3af' }}>
                {stats?.credits.credits_spent ?? 0} credits spent this period
              </p>
            </div>

            {/* Review ratings */}
            {stats?.reviews.total != null && stats.reviews.total > 0 && (
              <div style={{ background: '#fff', borderRadius: 16, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#111827' }}>Review ratings</p>
                  <span style={{ fontSize: 20, color: '#f59e0b', fontWeight: 800 }}>
                    {stats.reviews.avg_overall?.toFixed(1) ?? '—'}★
                  </span>
                </div>
                <RatingRow label="Quality of work"  value={stats.reviews.avg_quality} />
                <RatingRow label="Communication"    value={stats.reviews.avg_communication} />
                <RatingRow label="Timeliness"       value={stats.reviews.avg_timeliness} />
                <RatingRow label="Price fairness"   value={stats.reviews.avg_price_fairness} />
                <p style={{ margin: '8px 0 0', fontSize: 12, color: '#9ca3af' }}>
                  Based on {stats.reviews.total} review{stats.reviews.total === 1 ? '' : 's'}
                </p>
              </div>
            )}

            {/* Win rate & benchmarks */}
            {wr && (
              <>
                <div style={{ background: '#fff', borderRadius: 16, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
                  <p style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 700, color: '#111827' }}>
                    Lead performance
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <StatCard label="Accept rate" value={`${wr.lead_performance.acceptance_pct}%`} accent="#22c55e" />
                    <StatCard label="Leads total" value={String(wr.lead_performance.total)} />
                    <StatCard label="Avg response" value={wr.response_time.avg_hours != null ? `${wr.response_time.avg_hours.toFixed(1)}h` : '—'} />
                    <StatCard label="Best day" value={wr.best_time.day_of_week ?? '—'} {...(wr.best_time.wins > 0 ? { sub: `${wr.best_time.wins} wins` } : {})} accent="#8b5cf6" />
                  </div>
                </div>

                {/* Anonymized benchmarks */}
                <div style={{ background: '#fff', borderRadius: 16, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
                  <p style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 700, color: '#111827' }}>Area benchmarks</p>
                  <p style={{ margin: '0 0 12px', fontSize: 11, color: '#9ca3af' }}>Anonymous averages of verified contractors in your ZIP codes</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {[
                      { label: 'Win rate', yours: wr.benchmarks.your_win_rate, area: wr.benchmarks.area_win_rate, fmt: (v: number) => `${v}%` },
                      { label: 'Avg response', yours: wr.benchmarks.your_avg_response_hours, area: wr.benchmarks.area_avg_response_hours, fmt: (v: number) => `${v.toFixed(1)}h` },
                    ].map(({ label, yours, area, fmt }) => (
                      <div key={label}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                          <span style={{ fontSize: 13, color: '#374151' }}>{label}</span>
                          <span style={{ fontSize: 13, color: '#9ca3af' }}>
                            You: <strong style={{ color: '#111827' }}>{yours != null ? fmt(yours) : '—'}</strong>
                            {area != null && <> · Area avg: <strong style={{ color: '#6b7280' }}>{fmt(area)}</strong></>}
                          </span>
                        </div>
                        {yours != null && area != null && (
                          <div style={{ height: 6, background: '#f3f4f6', borderRadius: 99, overflow: 'hidden', position: 'relative' }}>
                            <div style={{ width: `${Math.min(100, (yours / Math.max(yours, area)) * 100)}%`, height: '100%', background: yours >= area ? '#22c55e' : '#f59e0b', borderRadius: 99 }} />
                          </div>
                        )}
                      </div>
                    ))}
                    {wr.benchmarks.area_avg_job_value != null && (
                      <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>
                        Area avg job value: <strong>${wr.benchmarks.area_avg_job_value.toLocaleString()}</strong>
                      </p>
                    )}
                  </div>
                </div>

                {/* Revenue by trade */}
                {wr.revenue_by_trade.length > 0 && (
                  <div style={{ background: '#fff', borderRadius: 16, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
                    <p style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 700, color: '#111827' }}>Revenue by trade</p>
                    {wr.revenue_by_trade.map(r => (
                      <div key={r.trade} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                        <span style={{ fontSize: 13, color: '#374151', textTransform: 'capitalize' }}>{r.trade.replace(/_/g, ' ')}</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>${r.total.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Seasonal forecast */}
                {wr.seasonal_forecast.length > 0 && (
                  <div style={{ background: '#fff', borderRadius: 16, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
                    <p style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 700, color: '#111827' }}>Seasonal forecast</p>
                    <p style={{ margin: '0 0 12px', fontSize: 11, color: '#9ca3af' }}>Pending tasks in your area due in the next 60 days</p>
                    {wr.seasonal_forecast.map(f => (
                      <div key={f.category} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f3f4f6' }}>
                        <span style={{ fontSize: 13, color: '#374151', textTransform: 'capitalize' }}>{f.category.replace(/_/g, ' ')}</span>
                        <span style={{ fontSize: 12, background: '#eff6ff', color: '#1D4ED8', fontWeight: 700, borderRadius: 20, padding: '2px 10px' }}>
                          {f.upcoming_count} upcoming
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
